import { useState, useEffect } from 'react';
import { X, Ellipsis, Trash2 } from '@/components/icons';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
} from './ui/drawer';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  PrntrCard,
  PrntrInput,
  PrntrTextareaShell,
  PrntrValue,
  prntrInputClass,
  prntrTextareaClass,
} from './ui/prntr-input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useNoteGetById,
  useNoteAdd,
  useNoteUpdate,
  useNoteDelete,
  useCrewNoteAdd,
  useCrewNoteUpdate,
  useCrewNoteDelete,
  useCrewNotesList,
} from '../lib/hooks';

export type NoteParent = { tripId: string } | { crewId: string };

interface NoteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  parent: NoteParent;
  noteId?: string | null;
  readOnly?: boolean;
}

export function NoteDrawer({
  isOpen,
  onClose,
  parent,
  noteId,
  readOnly = false,
}: NoteDrawerProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const isEditMode = !!noteId;
  const isCrewMode = 'crewId' in parent;

  // Trip-note hooks
  const { data: tripNote, isLoading: isLoadingTripNote } = useNoteGetById(
    !isCrewMode ? (noteId ?? undefined) : undefined,
  );
  const { mutate: addTripNote, isPending: isAddingTrip } = useNoteAdd(
    isCrewMode ? '' : parent.tripId,
  );
  const { mutate: updateTripNote, isPending: isUpdatingTrip } = useNoteUpdate(
    isCrewMode ? '' : parent.tripId,
  );
  const { mutate: deleteTripNote, isPending: isDeletingTrip } = useNoteDelete(
    isCrewMode ? '' : parent.tripId,
  );

  // Crew-note hooks
  const { data: crewNotes } = useCrewNotesList();
  const crewNote = isCrewMode && noteId ? crewNotes?.find((n) => n.id === noteId) : undefined;
  const { mutate: addCrewNote, isPending: isAddingCrew } = useCrewNoteAdd();
  const { mutate: updateCrewNote, isPending: isUpdatingCrew } = useCrewNoteUpdate();
  const { mutate: deleteCrewNote, isPending: isDeletingCrew } = useCrewNoteDelete();

  const isPending = isAddingTrip || isUpdatingTrip || isDeletingTrip || isAddingCrew || isUpdatingCrew || isDeletingCrew;
  const note = isCrewMode ? crewNote : tripNote;
  const isLoadingNote = !isCrewMode && isLoadingTripNote;

  useEffect(() => {
    if (isOpen && isEditMode && note) {
      setTitle(note.title ?? '');
      setBody(note.body);
    } else if (isOpen && !isEditMode) {
      setTitle('');
      setBody('');
    }
  }, [isOpen, isEditMode, note]);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setBody('');
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    const onSuccess = (label: string) => () => {
      toast.success(label);
      onClose();
    };
    const onError = (label: string) => (error: unknown) => {
      console.error(label, error);
      toast.error(label);
    };

    if (isEditMode && noteId) {
      if (isCrewMode) {
        updateCrewNote(
          { id: noteId, title: title.trim(), body: body.trim() },
          { onSuccess: onSuccess('Note updated'), onError: onError('Failed to update note') },
        );
      } else {
        updateTripNote(
          { id: noteId, title: title.trim(), body: body.trim() },
          { onSuccess: onSuccess('Note updated'), onError: onError('Failed to update note') },
        );
      }
    } else {
      if (isCrewMode) {
        addCrewNote(
          { title: title.trim(), body: body.trim() },
          { onSuccess: onSuccess('Note added'), onError: onError('Failed to add note') },
        );
      } else {
        addTripNote(
          { tripId: parent.tripId, title: title.trim(), body: body.trim() },
          { onSuccess: onSuccess('Note added'), onError: onError('Failed to add note') },
        );
      }
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = () => {
    if (!noteId) return;

    const onSuccess = () => {
      toast.success('Note deleted');
      onClose();
    };
    const onError = (error: unknown) => {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    };

    if (isCrewMode) {
      deleteCrewNote({ id: noteId }, { onSuccess, onError });
    } else {
      deleteTripNote({ id: noteId }, { onSuccess, onError });
    }
  };

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
        {/* Header */}
        <DrawerHeader className="flex flex-row items-center justify-between p-4">
          <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
            {readOnly ? 'View Note' : isEditMode ? 'Edit Note' : 'Add Note'}
          </DrawerTitle>
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="icon" roundness="round" className="size-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DrawerHeader>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          {isLoadingNote && isEditMode ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-content-primary-subtle">Loading...</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <PrntrCard>
                <PrntrInput label="Title" empty={!title}>
                  {readOnly ? (
                    <PrntrValue value={title} placeholder="—" />
                  ) : (
                    <input
                      id="note-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter title"
                      className={prntrInputClass}
                    />
                  )}
                </PrntrInput>
              </PrntrCard>

              <PrntrTextareaShell label="Details">
                <Textarea
                  id="note-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Enter details"
                  readOnly={readOnly}
                  className={cn(prntrTextareaClass, readOnly && 'pointer-events-none')}
                />
              </PrntrTextareaShell>
            </div>
          )}
        </div>

        {/* Footer */}
        <DrawerFooter className="flex flex-row gap-2 p-4">
          <Button
            variant="secondary"
            size="mini"
            onClick={handleCancel}
            className="flex-1"
            disabled={isPending}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              variant="primary"
              size="mini"
              onClick={handleSave}
              className="flex-1"
              disabled={isPending || !title.trim()}
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
